/**
 * Typed reader for the Phaser `multiatlas` JSON the normalizer emits per tier
 * (public/assets/georgi/n1/<tier>/georgi-n1-<tier>.multiatlas.json).
 * Pure TypeScript; used by unit tests to pin the committed atlases and by the loader for path math.
 */
import { N1_PIVOT } from './n1Manifest';

export interface AtlasRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface AtlasFrame {
  filename: string;
  frame: AtlasRect;
  rotated: boolean;
  trimmed: boolean;
  spriteSourceSize: AtlasRect;
  sourceSize: { w: number; h: number };
  pivot: { x: number; y: number };
}

export interface AtlasTexture {
  image: string;
  format: string;
  size: { w: number; h: number };
  scale: number;
  frames: AtlasFrame[];
}

export interface Multiatlas {
  textures: AtlasTexture[];
  meta: Record<string, unknown>;
}

export const MAX_ATLAS_PAGE = 2048;

const isPot = (v: number) => v > 0 && (v & (v - 1)) === 0;

/** Validate a multiatlas document. Returns the list of problems (empty = valid). */
export function multiatlasProblems(doc: Multiatlas, expectedFrames: readonly string[]): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const tex of doc.textures) {
    if (tex.size.w > MAX_ATLAS_PAGE || tex.size.h > MAX_ATLAS_PAGE)
      problems.push(`${tex.image}: page ${tex.size.w}x${tex.size.h} exceeds ${MAX_ATLAS_PAGE}`);
    if (!isPot(tex.size.w) || !isPot(tex.size.h))
      problems.push(`${tex.image}: page ${tex.size.w}x${tex.size.h} is not power-of-two`);
    const rects: AtlasRect[] = [];
    for (const f of tex.frames) {
      if (seen.has(f.filename)) problems.push(`${f.filename}: duplicate frame`);
      seen.add(f.filename);
      if (f.rotated) problems.push(`${f.filename}: rotated frames are not allowed`);
      if (f.pivot.x !== N1_PIVOT.x || f.pivot.y !== N1_PIVOT.y)
        problems.push(
          `${f.filename}: pivot ${f.pivot.x},${f.pivot.y} != ${N1_PIVOT.x},${N1_PIVOT.y}`,
        );
      const r = f.frame;
      if (r.x < 0 || r.y < 0 || r.x + r.w > tex.size.w || r.y + r.h > tex.size.h)
        problems.push(`${f.filename}: frame rect leaves the page`);
      const s = f.spriteSourceSize;
      if (s.w !== r.w || s.h !== r.h)
        problems.push(`${f.filename}: spriteSourceSize does not match frame`);
      if (s.x < 0 || s.y < 0 || s.x + s.w > f.sourceSize.w || s.y + s.h > f.sourceSize.h)
        problems.push(`${f.filename}: sprite leaves its source page`);
      for (const o of rects)
        if (r.x < o.x + o.w && o.x < r.x + r.w && r.y < o.y + o.h && o.y < r.y + r.h) {
          problems.push(`${f.filename}: overlaps another frame on ${tex.image}`);
          break;
        }
      rects.push(r);
    }
  }
  for (const name of expectedFrames)
    if (!seen.has(name)) problems.push(`${name}: missing from atlas`);
  for (const name of seen)
    if (!expectedFrames.includes(name)) problems.push(`${name}: unexpected frame`);
  return problems;
}

/** Directory part of a multiatlas URL — Phaser needs it as the image path. */
export function atlasDirectory(multiatlasUrl: string): string {
  const i = multiatlasUrl.lastIndexOf('/');
  return i < 0 ? '' : multiatlasUrl.slice(0, i);
}
